/**
 * MessageRouter — deserializes incoming WebSocket JSON envelopes
 * and dispatches them to registered handlers by messageType.
 */

import type { WsResponse, WsErrorResponse, WsMessageType } from '../types';
import { isWsError } from '../types';

type MessageHandler = (payload: unknown, correlationId: string | null) => void;
type ErrorHandler = (error: { code: string; message: string }, correlationId: string | null) => void;

export class MessageRouter {
  private handlers = new Map<string, MessageHandler>();
  private errorHandler: ErrorHandler | null = null;
  private pendingResponses = new Map<string, {
    resolve: (payload: unknown) => void;
    reject: (error: { code: string; message: string }) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();

  /** Register a handler for a specific messageType */
  onMessage(messageType: string, handler: MessageHandler): void {
    this.handlers.set(messageType, handler);
  }

  /** Register the global error handler */
  onError(handler: ErrorHandler): void {
    this.errorHandler = handler;
  }

  /**
   * Register a pending request by correlationId.
   * Returns a promise that resolves with the payload or rejects on error/timeout.
   */
  expectResponse(correlationId: string, timeoutMs = 10_000): Promise<unknown> {
    return new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingResponses.delete(correlationId);
        reject({ code: 'timeout', message: 'Request timed out' });
      }, timeoutMs);

      this.pendingResponses.set(correlationId, { resolve, reject, timer });
    });
  }

  /** Process a raw JSON string from WebSocket. */
  route(raw: string): void {
    let msg: WsResponse | WsErrorResponse;
    try {
      msg = JSON.parse(raw) as WsResponse | WsErrorResponse;
    } catch (e) {
      console.error('[MessageRouter] Invalid JSON:', raw);
      return;
    }

    const { messageType, correlationId } = msg;
    const corrId = correlationId ?? null;

    // Check for pending response first
    if (corrId && this.pendingResponses.has(corrId)) {
      const pending = this.pendingResponses.get(corrId)!;
      clearTimeout(pending.timer);
      this.pendingResponses.delete(corrId);

      if (isWsError(msg)) {
        pending.reject(msg.error);
      } else {
        pending.resolve(msg.payload);
      }
      // Still route to regular handlers below
    }

    // Error envelope
    if (isWsError(msg)) {
      this.errorHandler?.(msg.error, corrId);
      // Also dispatch to 'error' message handler if registered
      const handler = this.handlers.get('error');
      handler?.(msg.error, corrId);
      return;
    }

    // Normal message — dispatch by messageType
    const handler = this.handlers.get(messageType);
    if (handler) {
      try {
        handler(msg.payload, corrId);
      } catch (e) {
        console.error(`[MessageRouter] Error in handler for '${messageType}':`, e);
      }
    } else {
      console.debug(`[MessageRouter] No handler for messageType '${messageType}'`);
    }
  }

  /** Clean up pending responses */
  destroy(): void {
    for (const [, pending] of this.pendingResponses) {
      clearTimeout(pending.timer);
      pending.reject({ code: 'destroyed', message: 'Router destroyed' });
    }
    this.pendingResponses.clear();
    this.handlers.clear();
  }
}
